// One-shot reload guard for failed lazy-chunk loads (Sentry LUTIQ-74).
//
// When a dynamic `import()` of a code-split chunk fails (transient network
// blip, CDN hiccup, or a deploy swapping hashed filenames), Vite dispatches a
// cancelable `vite:preloadError` event on `window` before throwing. Left
// unhandled, the throw crashes the React tree and strands the user on the
// root ErrorBoundary fallback — even though a plain reload fixes it.
//
// The guard reloads the page ONCE per failure burst: the first failure calls
// `event.preventDefault()` (so Vite swallows the error) and reloads. If
// another chunk fails within RELOAD_GUARD_WINDOW_MS of that reload — i.e. the
// reload didn't help — the event is left untouched, the error propagates to
// the ErrorBoundary exactly as before, and no reload loop is possible.
//
// The flag is the timestamp of the last automatic reload, kept in
// sessionStorage (survives the reload, scoped to the tab, gone when the tab
// closes). A flag older than the window counts as "no recent reload", so a
// later unrelated failure earns a fresh one-shot reload without us having to
// hook successful loads to clear it.

const RELOAD_FLAG_KEY = 'lutiq:chunk-reload:last-reload-at';
const RELOAD_GUARD_WINDOW_MS = 60_000;

const readLastReloadAt = (): number | null => {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (raw === null) return null;
    // Clamp malformed values to "no flag" so garbage can't wedge the guard —
    // same NaN-safety rationale as keycloak.ts's recovery counter.
    const at = Number(raw);
    return Number.isFinite(at) ? at : null;
  } catch {
    // sessionStorage unavailable — report "unknown"; the write below decides.
    return null;
  }
};

const writeLastReloadAt = (at: number): boolean => {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(at));
    return true;
  } catch {
    return false;
  }
};

export const handlePreloadError = (event: Event): void => {
  const now = Date.now();
  const lastReloadAt = readLastReloadAt();
  // Strict `now - last < window` (not an absolute difference): a future-dated
  // flag (clock stepped backwards) also suppresses the reload — the safe
  // direction, since falling through only shows the fallback's Reload button.
  if (lastReloadAt !== null && now - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
    // We already spent the one automatic reload for this burst and a chunk
    // STILL failed — let the error reach the ErrorBoundary instead of looping.
    return;
  }
  if (!writeLastReloadAt(now)) {
    // Couldn't persist the one-shot flag; reloading anyway could loop
    // forever, so fall through to the ErrorBoundary instead.
    return;
  }
  event.preventDefault();
  window.location.reload();
};

// Must run before the first lazy chunk can fail — main.tsx calls it at module
// scope, right after initSentry(). Passing the same module-level handler means
// addEventListener dedupes an accidental double install.
export const installChunkReloadGuard = (): void => {
  window.addEventListener('vite:preloadError', handlePreloadError);
};
